import {
  type FlowHumanTaskToolClinicalQuestionAssistantConfig,
  type FlowPriorAuthorizationInformation,
} from 'flow-schema';

import { isTruthy } from 'common';

interface GetCustomClinicalQuestionAssistantConfigOptions {
  existingConfig: FlowHumanTaskToolClinicalQuestionAssistantConfig | undefined;
  priorAuthorizationInformation: FlowPriorAuthorizationInformation | undefined;
}

/**
 * Maps Prior Authorization assistant fields into the tools-based Custom task contract.
 *
 * @param options - Existing Custom config and source Prior Authorization information.
 * @returns Custom Clinical Question Assistant config with mapped source values and preserved dormant values.
 */
export function getCustomClinicalQuestionAssistantConfig(
  options: GetCustomClinicalQuestionAssistantConfigOptions
): FlowHumanTaskToolClinicalQuestionAssistantConfig {
  const { existingConfig, priorAuthorizationInformation } = options;
  const copilotDataContextObject = priorAuthorizationInformation?.copilotDataContextObject;
  const legacyCopilotDataContext = priorAuthorizationInformation?.copilotDataContext;
  const dataContext = isTruthy(copilotDataContextObject?.trim())
    ? copilotDataContextObject
    : isTruthy(legacyCopilotDataContext?.trim())
      ? `{"documents": ${legacyCopilotDataContext}}`
      : undefined;
  const predictionOptions = priorAuthorizationInformation?.predictionOptions;

  return {
    ...existingConfig,
    dataContext: dataContext ?? existingConfig?.dataContext,
    preamble: predictionOptions?.preamble ?? existingConfig?.preamble,
    outputRequirements: predictionOptions?.outputRequirement ?? existingConfig?.outputRequirements,
    additionalInstructions: predictionOptions?.outro ?? existingConfig?.additionalInstructions,
    modelName: priorAuthorizationInformation?.modelName ?? existingConfig?.modelName,
  };
}

interface GetPriorAuthorizationInformationOptions {
  clinicalQuestionAssistantConfig: FlowHumanTaskToolClinicalQuestionAssistantConfig | undefined;
  priorAuthorizationInformation: FlowPriorAuthorizationInformation | undefined;
}

/**
 * Maps tools-based Custom task settings into the existing Prior Authorization contract.
 *
 * @param options - Source Custom config and existing Prior Authorization information.
 * @returns Prior Authorization information with mapped Clinical Question Assistant settings.
 */
export function getPriorAuthorizationInformationWithClinicalQuestionAssistantConfig(
  options: GetPriorAuthorizationInformationOptions
): FlowPriorAuthorizationInformation {
  const { clinicalQuestionAssistantConfig, priorAuthorizationInformation } = options;
  const existingPredictionOptions = priorAuthorizationInformation?.predictionOptions;

  return {
    ...priorAuthorizationInformation,
    patientOrderId: priorAuthorizationInformation?.patientOrderId ?? '',
    copilotDataContextObject:
      clinicalQuestionAssistantConfig?.dataContext ??
      priorAuthorizationInformation?.copilotDataContextObject,
    modelName:
      clinicalQuestionAssistantConfig?.modelName ?? priorAuthorizationInformation?.modelName,
    predictionOptions: {
      ...existingPredictionOptions,
      preamble: clinicalQuestionAssistantConfig?.preamble ?? existingPredictionOptions?.preamble,
      outputRequirement:
        clinicalQuestionAssistantConfig?.outputRequirements ??
        existingPredictionOptions?.outputRequirement,
      outro:
        clinicalQuestionAssistantConfig?.additionalInstructions ?? existingPredictionOptions?.outro,
    },
  };
}
